import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useTable } from "@/lib/store";
import { Card, PageHead, Kicker, Chip, Avatar, Empty } from "@/components/kit";
import { fmtDate } from "@/lib/util";
import { toast } from "sonner";
import { ArrowUpRight, Trophy, FileDown } from "lucide-react";

const STATUS_COLOR = {
  Confirmed: "var(--green)",
  Submitted: "var(--brass)",
  "In progress": "var(--amber)",
  "Not started": undefined,
};

const dayColor = (d) =>
  d === null || d === undefined ? "var(--ink-mute)"
    : d <= 7 ? "var(--rust)" : d <= 30 ? "var(--amber)" : "var(--green)";

export const useCup = () => {
  const { yearId } = useTable();
  const [cup, setCup] = useState(null);
  const load = useCallback(async () => {
    if (!yearId) return;
    try { setCup((await api.get("/cup", { params: { yearId } })).data); }
    catch (e) { toast.error(errMsg(e)); }
  }, [yearId]);
  useEffect(() => { load(); }, [load]);
  return { cup, reload: load };
};

const Dial = ({ banked, projected, max }) => {
  const pctB = Math.min(100, (banked / max) * 100);
  const pctP = Math.min(100, (projected / max) * 100);
  const r = 54, circ = 2 * Math.PI * r;
  return (
    <div className="relative shrink-0" style={{ width: 140, height: 140 }} data-testid="cup-dial">
      <svg width="140" height="140" viewBox="0 0 140 140" style={{ transform: "rotate(-90deg)" }}>
        <circle cx="70" cy="70" r={r} fill="none" stroke="var(--hairline)" strokeWidth="9" />
        <circle cx="70" cy="70" r={r} fill="none" stroke="var(--brass)" strokeWidth="9"
          strokeDasharray={circ} strokeDashoffset={circ - (circ * pctP) / 100}
          strokeLinecap="round" opacity="0.32"
          style={{ transition: "stroke-dashoffset .6s var(--ease)" }} />
        <circle cx="70" cy="70" r={r} fill="none" stroke="var(--green)" strokeWidth="9"
          strokeDasharray={circ} strokeDashoffset={circ - (circ * pctB) / 100}
          strokeLinecap="round" style={{ transition: "stroke-dashoffset .6s var(--ease)" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="numeral text-3xl leading-none" data-testid="cup-projected">{projected}</span>
        <span className="text-[10px] uppercase tracking-[0.14em] mt-1" style={{ color: "var(--ink-mute)" }}>
          of {max}
        </span>
      </div>
    </div>
  );
};

export default function Cup() {
  const { cup } = useCup();
  const { memberById, yearId } = useTable();
  const [downloading, setDownloading] = useState(false);
  if (!cup) return <Empty line="Loading the Cup…" />;
  const t = cup.totals;

  const report = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/cup/report.pdf?yearId=${yearId}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `forum-cup-${yearId}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Report downloaded.");
    } catch (e) { toast.error(errMsg(e)); }
    setDownloading(false);
  };

  return (
    <div data-testid="cup-page">
      <PageHead
        kicker={`Forum Cup · 10 criteria · ${t.max} points`}
        title="Forum Cup Command Centre"
        sub="Write down what happened, claim the points, and send the Forum Officer a report. The Cup runs on trust — no proof is required."
        right={
          <button className="btn-ink" disabled={downloading} data-testid="cup-report"
            onClick={report}>
            <FileDown size={14} /> {downloading ? "Preparing…" : "Report for YPO"}
          </button>
        }
      />

      <Card className="!p-0 overflow-hidden mb-6">
        <div className="grid lg:grid-cols-[auto_1fr]">
          <div className="p-6 sm:p-8 flex items-center gap-6">
            <Dial banked={t.banked} projected={t.projected} max={t.max} />
            <div className="space-y-3">
              {[["Banked", t.banked, "var(--green)"],
                ["Submitted", t.submitted, "var(--brass)"],
                ["Projected", t.projected, "var(--ink)"],
                ["At risk", t.atRisk, "var(--rust)"]].map(([label, val, color]) => (
                <div key={label} className="flex items-baseline gap-3">
                  <span className="numeral text-lg" style={{ color }} data-testid={`cup-total-${label.toLowerCase().replace(" ", "-")}`}>
                    {val}
                  </span>
                  <span className="text-[11px] uppercase tracking-[0.12em]" style={{ color: "var(--ink-mute)" }}>
                    {label}
                  </span>
                </div>
              ))}
            </div>
          </div>
          <div className="p-6 sm:p-8" style={{ background: "var(--paper-deep)", borderLeft: "1px solid var(--hairline)" }}>
            <Kicker className="!text-[9px]">Next three deadlines</Kicker>
            <div className="mt-4 space-y-3">
              {cup.deadlines.map((d) => (
                <Link key={d.criterionId} to={`/cup/${d.criterionId}`}
                  data-testid={`cup-deadline-${d.num}`}
                  className="flex items-center gap-3 hover:opacity-70 transition-opacity">
                  <span className="numeral text-xl" style={{ color: dayColor(d.daysLeft), minWidth: 46 }}>
                    {d.daysLeft}d
                  </span>
                  <span className="text-sm min-w-0 flex-1 truncate">C{d.num} {d.name}</span>
                  <span className="text-xs" style={{ color: "var(--ink-mute)" }}>{fmtDate(d.deadline)}</span>
                </Link>
              ))}
            </div>
            <div className="hair my-5" />
            <Kicker className="!text-[9px]">This week</Kicker>
            <ul className="mt-3 space-y-2">
              {cup.coach.slice(0, 3).map((c) => (
                <li key={c.num} className="text-[13.5px] flex gap-2" data-testid={`cup-coach-${c.num}`}>
                  <span style={{ color: "var(--brass)" }}>—</span>
                  <span>{c.line}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Card>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="cup-grid">
        {cup.criteria.map((c) => (
          <Link key={c.id} to={`/cup/${c.id}`} data-testid={`cup-card-${c.num}`}>
            <Card className="p-5 h-full flex flex-col hover:border-[var(--brass-bright)] transition-colors">
              <div className="flex items-start gap-3">
                <span className="roman text-2xl leading-none" style={{ color: "var(--brass)" }}>
                  {c.num}
                </span>
                <div className="min-w-0 flex-1">
                  <h3 className="serif text-lg leading-snug">{c.name}</h3>
                  <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                    {(c.ownerIds || []).map((id) => {
                      const m = memberById(id);
                      return m ? <Avatar key={id} name={m.name} color={m.avatarColor} size={20} /> : null;
                    })}
                    <span className="text-[11.5px]" style={{ color: "var(--ink-mute)" }}>
                      {(c.ownerIds || []).map((id) => memberById(id)?.name.split(" ")[0]).filter(Boolean).join(", ") || "Unassigned"}
                    </span>
                  </div>
                </div>
                <span className="numeral text-sm whitespace-nowrap" data-testid={`cup-points-${c.num}`}>
                  {c.awarded}/{c.maxPoints}
                </span>
              </div>
              <p className="text-[13px] mt-3 leading-relaxed" style={{ color: "var(--ink-soft)" }}>
                {c.nextAction}
              </p>
              <div className="mt-auto pt-4 flex items-center gap-2 flex-wrap">
                <Chip color={STATUS_COLOR[c.status]}>{c.status}</Chip>
                {c.notes ? null : <Chip color="var(--ink-mute)">No record yet</Chip>}
                {c.missed ? <Chip color="var(--rust)">Overdue</Chip>
                  : c.atRisk ? <Chip color="var(--amber)">At risk</Chip> : null}
                <span className="ml-auto text-[11.5px]" style={{ color: dayColor(c.daysLeft) }}>
                  {c.daysLeft === null ? "" : c.daysLeft < 0 ? "past due" : `${c.daysLeft}d`}
                </span>
              </div>
            </Card>
          </Link>
        ))}
      </div>

      <div className="mt-8 grid lg:grid-cols-2 gap-4">
        <Card className="p-5" data-testid="cup-forms">
          <Kicker>Meeting form tracker</Kicker>
          {cup.forms.length === 0 ? (
            <p className="text-sm mt-3" style={{ color: "var(--ink-mute)" }}>
              No chapters with dates yet.
            </p>
          ) : (
            <div className="mt-3 divide-hair">
              {cup.forms.map((f) => {
                const left = Math.ceil((new Date(f.dueDate) - new Date()) / 86400000);
                return (
                  <div key={f.id} className="flex items-center gap-3 py-2.5" data-testid={`cup-form-${f.id}`}>
                    <span className="text-sm min-w-0 flex-1">{f.label}</span>
                    <span className="text-xs" style={{ color: "var(--ink-mute)" }}>
                      due {fmtDate(f.dueDate)}
                    </span>
                    {f.filed
                      ? <Chip color="var(--green)">Filed</Chip>
                      : <Chip color={dayColor(left)}>{left < 0 ? "overdue" : `${left}d`}</Chip>}
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        <Card className="p-5" data-testid="cup-calendar">
          <Kicker>Booking triggers</Kicker>
          <div className="mt-3 divide-hair">
            {cup.triggers.map((t) => (
              <div key={t.key} className="flex items-center gap-3 py-2.5">
                <span className="text-sm min-w-0 flex-1">{t.label}</span>
                <span className="text-xs" style={{ color: "var(--ink-mute)" }}>{fmtDate(t.by)}</span>
                <Chip color={dayColor(t.daysLeft)}>
                  {t.daysLeft < 0 ? "passed" : `${t.daysLeft}d`}
                </Chip>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div className="mt-8 flex items-center gap-2 text-[12.5px]" style={{ color: "var(--ink-mute)" }}>
        <Trophy size={13} style={{ color: "var(--brass)" }} />
        The platform never submits anything to YPO. It keeps the record and the reminders.
        <ArrowUpRight size={12} />
      </div>
    </div>
  );
}
